import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiUrl } from '../config/paths';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Divider,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PrintIcon from '@mui/icons-material/Print';

interface DutyShiftReportData {
  period: { from: string; to: string; label: string };
  signals: {
    total: number;
    byType: Record<string, number>;
    matched: number;
    unmatched: number;
  };
  requests: {
    total: number;
    fromEmail: number;
    fromWebform: number;
    confirmed: number;
    rejected: number;
    pending: number;
    autoConfirmed: number;
  };
  confirmations: { sent: number; failed: number };
  emails: { received: number; parsed: number; pendingReview: number; failed: number };
  events: Array<{
    time: string;
    type: 'signal' | 'request' | 'confirmation' | 'rejection' | 'alert';
    description: string;
  }>;
}

function computeCurrentShiftUtc(): { from: Date; to: Date } {
  const now = new Date();
  const hour = now.getUTCHours();
  const dutyStart = new Date(now);
  if (hour >= 5) {
    dutyStart.setUTCHours(5, 0, 0, 0);
  } else {
    dutyStart.setUTCDate(dutyStart.getUTCDate() - 1);
    dutyStart.setUTCHours(5, 0, 0, 0);
  }
  const dutyEnd = new Date(dutyStart);
  dutyEnd.setUTCDate(dutyEnd.getUTCDate() + 1);
  return { from: dutyStart, to: dutyEnd };
}

function formatEventTime(iso: string): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())} ${pad(d.getUTCDate())}.${pad(
      d.getUTCMonth() + 1
    )} UTC`;
  } catch {
    return iso;
  }
}

const typeChipColor: Record<
  DutyShiftReportData['events'][number]['type'],
  'default' | 'primary' | 'success' | 'error' | 'warning' | 'info'
> = {
  signal: 'info',
  request: 'primary',
  confirmation: 'success',
  rejection: 'error',
  alert: 'error',
};

const typeChipLabel: Record<DutyShiftReportData['events'][number]['type'], string> = {
  signal: 'Сигнал',
  request: 'Заявка',
  confirmation: 'Подтверждение',
  rejection: 'Отказ',
  alert: 'Тревога',
};

const Card: React.FC<{
  label: string;
  value: number | string;
  color?: string;
}> = ({ label, value, color }) => (
  <Paper sx={{ p: 2, textAlign: 'center', height: '100%' }} elevation={1}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="h5" fontWeight="bold" sx={{ color: color || 'text.primary' }}>
      {value}
    </Typography>
  </Paper>
);

const DutyShiftReport: React.FC = () => {
  const [params] = useSearchParams();
  const [data, setData] = useState<DutyShiftReportData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { from, to } = useMemo(() => {
    const fromParam = params.get('from');
    const toParam = params.get('to');
    if (fromParam && toParam) {
      const fd = new Date(fromParam);
      const td = new Date(toParam);
      if (!isNaN(fd.getTime()) && !isNaN(td.getTime())) {
        return { from: fd, to: td };
      }
    }
    return computeCurrentShiftUtc();
  }, [params]);

  const operatorName = useMemo(() => {
    try {
      const saved = localStorage.getItem('operator_info');
      if (!saved) return null;
      const parsed = JSON.parse(saved);
      return parsed?.fullName || parsed?.full_name || null;
    } catch {
      return null;
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};
      const url = apiUrl(
        `duty/shift-report?from=${encodeURIComponent(from.toISOString())}&to=${encodeURIComponent(
          to.toISOString()
        )}`
      );
      const res = await fetch(url, { headers });
      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json?.message || `Ошибка загрузки отчёта: ${res.status}`);
      }
      setData(json.data);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Не удалось загрузить отчёт');
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <style>{`
        @media print {
          [data-print="hide"] { display: none !important; }
          body { background: #fff; }
        }
      `}</style>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: 3,
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Отчёт за смену
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Период: {data?.period.label || '—'}
          </Typography>
          {operatorName && (
            <Typography variant="body2" color="text.secondary">
              Оперативный дежурный: {operatorName}
            </Typography>
          )}
        </Box>
        <Box data-print="hide" sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
            Обновить
          </Button>
          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={() => window.print()}
            disabled={loading || !data}
          >
            Печать
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {data && !loading && (
        <>
          {/* ========== БЛОК 1: Сигналы ========== */}
          <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
            Сигналы
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} md={3}>
              <Card label="Всего" value={data.signals.total} />
            </Grid>
            <Grid item xs={6} md={3}>
              <Card label="Сопоставлено" value={data.signals.matched} color="#2e7d32" />
            </Grid>
            <Grid item xs={6} md={3}>
              <Card label="Не сопоставлено" value={data.signals.unmatched} color="#ed6c02" />
            </Grid>
            <Grid item xs={6} md={3}>
              <Paper sx={{ p: 2, height: '100%' }} elevation={1}>
                <Typography variant="body2" color="text.secondary">
                  По типам
                </Typography>
                {Object.keys(data.signals.byType).length === 0 ? (
                  <Typography variant="body2">—</Typography>
                ) : (
                  Object.entries(data.signals.byType).map(([k, v]) => (
                    <Typography key={k} variant="body2">
                      {k}: <strong>{v}</strong>
                    </Typography>
                  ))
                )}
              </Paper>
            </Grid>
          </Grid>

          {/* ========== БЛОК 2: Заявки ========== */}
          <Typography variant="h6" sx={{ mb: 1 }}>
            Заявки
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} md={2}>
              <Card label="Всего" value={data.requests.total} />
            </Grid>
            <Grid item xs={6} md={2}>
              <Card label="Email" value={data.requests.fromEmail} />
            </Grid>
            <Grid item xs={6} md={2}>
              <Card label="Веб-форма" value={data.requests.fromWebform} />
            </Grid>
            <Grid item xs={6} md={2}>
              <Card label="Подтверждено" value={data.requests.confirmed} color="#2e7d32" />
            </Grid>
            <Grid item xs={6} md={2}>
              <Card label="Отклонено" value={data.requests.rejected} color="#d32f2f" />
            </Grid>
            <Grid item xs={6} md={2}>
              <Card label="В обработке" value={data.requests.pending} color="#ed6c02" />
            </Grid>
            <Grid item xs={6} md={2}>
              <Card label="Авто-подтверждено" value={data.requests.autoConfirmed} />
            </Grid>
          </Grid>

          {/* ========== БЛОК 3: Подтверждения ========== */}
          <Typography variant="h6" sx={{ mb: 1 }}>
            Подтверждения
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} md={3}>
              <Card label="Отправлено" value={data.confirmations.sent} color="#2e7d32" />
            </Grid>
            <Grid item xs={6} md={3}>
              <Card label="Ошибки" value={data.confirmations.failed} color="#d32f2f" />
            </Grid>
          </Grid>

          {/* ========== БЛОК 4: Почта ========== */}
          <Typography variant="h6" sx={{ mb: 1 }}>
            Электронная почта
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} md={3}>
              <Card label="Получено" value={data.emails.received} />
            </Grid>
            <Grid item xs={6} md={3}>
              <Card label="Разобрано" value={data.emails.parsed} color="#2e7d32" />
            </Grid>
            <Grid item xs={6} md={3}>
              <Card label="Ожидают" value={data.emails.pendingReview} color="#ed6c02" />
            </Grid>
            <Grid item xs={6} md={3}>
              <Card label="Ошибки" value={data.emails.failed} color="#d32f2f" />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* ========== БЛОК 5: Хронология ========== */}
          <Typography variant="h6" sx={{ mb: 1 }}>
            Хронология смены
          </Typography>
          <TableContainer component={Paper} elevation={1}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 170 }}>Время</TableCell>
                  <TableCell sx={{ width: 160 }}>Тип</TableCell>
                  <TableCell>Описание</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.events.map((ev, i) => (
                  <TableRow key={i}>
                    <TableCell>{formatEventTime(ev.time)}</TableCell>
                    <TableCell>
                      <Chip
                        label={typeChipLabel[ev.type]}
                        color={typeChipColor[ev.type]}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{ev.description}</TableCell>
                  </TableRow>
                ))}
                {data.events.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                      Событий за период нет
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
};

export default DutyShiftReport;
